<script setup lang="ts">
import type { ButtonProps } from '../../types/ui'

interface Props extends ButtonProps {}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  fullWidth: false,
  type: 'button',
})

const emit = defineEmits<{ click: [event: MouseEvent] }>()

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) emit('click', event)
}
</script>

<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="['btn', `btn-${variant}`, `btn-${size}`, { 'btn-block': fullWidth }]"
    @click="handleClick"
  >
    <svg v-if="loading" class="spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity=".25" />
      <path
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        opacity=".85"
      />
    </svg>
    <slot />
  </button>
</template>

<style scoped>
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border: 1px solid transparent;
  border-radius: 3px;
  font-family: var(--font-sans);
  font-weight: 500;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease,
    transform 0.2s var(--ease-out-quint);
}
.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* variants — accent is spent sparingly, so only `primary` uses it as a fill */
.btn-primary {
  background: var(--c-accent);
  color: var(--c-accent-ink);
}
.btn-primary:hover:not(:disabled) {
  background: var(--c-accent-hover);
}

.btn-secondary {
  background: var(--c-surface-invert);
  color: var(--c-ink-invert);
}

.btn-outline {
  border-color: var(--c-rule-strong);
  color: var(--c-ink);
}
.btn-outline:hover:not(:disabled) {
  border-color: var(--c-accent);
  color: var(--c-accent);
}

.btn-ghost {
  color: var(--c-accent);
}
.btn-ghost:hover:not(:disabled) {
  background: var(--c-accent-tint);
}

.btn-danger {
  background: #a02a1c;
  color: #fff;
}

/* sizes */
.btn-sm {
  padding: 0.4rem 0.8rem;
  font-size: var(--step--1);
}
.btn-md {
  padding: 0.6rem 1.1rem;
  font-size: var(--step-0);
}
.btn-lg {
  padding: 0.8rem 1.5rem;
  font-size: var(--step-1);
}
.btn-xl {
  padding: 1rem 2rem;
  font-size: var(--step-2);
}

.btn-block {
  width: 100%;
}

.spin {
  width: 1em;
  height: 1em;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
